/**
 * Resolves part numbers to their Parts Master descriptions.
 *
 * The spend indicators aggregate GRNs by part NUMBER (there is no FK from a
 * PO line to the catalog), but people read descriptions, not codes. This
 * hook looks the shown numbers up against the part catalog once each —
 * re-filtering never refetches a number already requested.
 *
 * @param partNumbers - the numbers currently on screen (order irrelevant)
 * @returns map of part number → catalog description ('' when unknown).
 *   A NEW object identity on every resolution, so memos downstream recompute.
 */
import { useEffect, useRef, useState } from 'react';
import { callOffAPI } from '../../lib/api';

export default function usePartDescriptions(
  partNumbers: string[],
): Record<string, string> {
  const [map, setMap] = useState<Record<string, string>>({});
  // Requested (in-flight or done) — lives outside state so a burst of
  // renders can't double-fetch the same number.
  const requested = useRef<Set<string>>(new Set());
  const key = partNumbers.filter(Boolean).sort().join('|');

  useEffect(() => {
    const missing = partNumbers.filter(
      (pn) => pn && !requested.current.has(pn),
    );
    if (!missing.length) return;
    missing.forEach((pn) => requested.current.add(pn));
    let cancelled = false;
    (async () => {
      const found = await Promise.all(missing.map(async (pn) => {
        try {
          const resp = await callOffAPI.getParts({ search: pn, page_size: 5 });
          const hit = (resp.data.items || []).find(
            (item) => item.part_number === pn,
          );
          return [pn, hit?.description || ''] as const;
        } catch {
          return [pn, ''] as const;
        }
      }));
      if (cancelled) return;
      setMap((prev) => {
        const next = { ...prev };
        found.forEach(([pn, desc]) => { next[pn] = desc; });
        return next;
      });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return map;
}
